import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import AlertDialog from "components/AlertDialog";
import DropdownSelect from "components/DropdownSelect";
import GroupedSelectMultiple from "components/GroupedSelectMultiple";
import LoadingButton from "components/LoadingButton";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDGroupedSelect from "components/MDGroupedSelect";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import MSelect from "components/MSelect";
import Dialog from "components/NoScrollDialog";
import { usePermissionMonitor } from "components/PermissionMonitor";
import TabPanel from "components/TabPanel";
import TranslatableTextField from "components/TranslatableTextField";
import { useAuth } from "contexts/auth.context";
import { useLanguage } from "contexts/language.context";
import DataTable from "examples/Tables/DataTable";
import * as React from "react";
import accountsService from "services/accounts-service";
import competencyService from "services/competency-service";
import kpisService from "services/kpis-service";
import pillarsService from "services/pillars-service";

// Reusable component for Account Selection
function AccountSelectionGrid({
  accountId,
  setAccountId,
  dropdownOptions,
  can,
  entitySetter,
}) {
  const { label } = useLanguage();
  return (
    <>
      <Grid item xs={can("isAdmin") ? 6 : 12}>
        <DropdownSelect
          label={label("account-singular")}
          items={dropdownOptions.accounts}
          initialValue={accountId || ""}
          disabled={can("isAdmin") ? !accountId : false}
          required
          onChange={(newValue) => {
            entitySetter((prev) => ({ ...prev, accountId: newValue?.id }));
          }}
        />
      </Grid>
      {can("isAdmin") ? (
        <Grid item xs={6}>
          <FormControlLabel
            label={label("Available for everyone")}
            control={
              <Checkbox
                checked={!accountId}
                onChange={(e) => {
                  if (!e.target.checked) {
                    entitySetter((prev) => ({
                      ...prev,
                      accountId: dropdownOptions?.accounts?.[0]?.id,
                    }));
                  } else {
                    entitySetter((prev) => ({ ...prev, accountId: null }));
                  }
                }}
              />
            }
          />
        </Grid>
      ) : null}
    </>
  );
}

// Reusable component for Risk/Action/Deviation table
function EditableItemsTable({
  title,
  items,
  onItemsChange,
  addButtonText,
  type = 0,
  minWidth = "400px",
}) {
  const { label } = useLanguage();
  const [open, setOpen] = React.useState({ alert: false });
  const handleAdd = () => {
    onItemsChange([
      ...items,
      {
        description: { en: "" },
        isDefault: items.length === 0,
        type: type,
      },
    ]);
  };

  const handleDefaultChange = (index, checked) => {
    const newItems = [...items];
    if (checked) {
      for (let i = 0; i < newItems.length; i++) {
        newItems[i].isDefault = false;
      }
    }
    newItems[index].isDefault = checked;
    onItemsChange([...newItems]);
  };

  const handleDescriptionChange = (index, value) => {
    const newItems = [...items];
    newItems[index].description = value;
    onItemsChange([...newItems]);
  };

  const handleDelete = (index) => {
    const newItems = [...items];
    const wasDefault = newItems[index].isDefault;
    newItems.splice(index, 1);

    if (newItems.length > 0 && wasDefault) {
      newItems[0].isDefault = true;
    }

    onItemsChange([...newItems]);
  };

  return (
    <Grid item xs={12}>
      <AlertDialog
        open={open.alert}
        setOpen={(value) => {
          setOpen((curr) => ({
            ...curr,
            alert: value,
          }));
        }}
        useNoScrollDialog={false}
        disableScrollLock={true}
        title={label("delete-alert-title")}
        content={label("delete-alert-message")}
        onOk={() => {
          handleDelete(open.alert.index);
        }}
      />
      <MDBox
        display="flex"
        flexDirection="row"
        justifyContent="start"
        alignItems="center"
      >
        <MDTypography variant="h6">{title}</MDTypography>
        <MDButton onClick={handleAdd}>
          <Icon>add</Icon>&nbsp; {addButtonText}
        </MDButton>
      </MDBox>
      <MDBox display="flex" flexDirection="column">
        <DataTable
          table={{
            columns: [
              {
                Header: label("Default"),
                accessor: "default",
                width: "10px",
                align: "center",
              },
              {
                Header: label("Description-singular"),
                accessor: "description",
                width: "100%",
              },
              {
                Header: label("Action-plural"),
                accessor: "actions",
                width: "15%",
                align: "center",
              },
            ],
            rows: items.map((value, index) => ({
              default: (
                <FormControlLabel
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                  control={
                    <Checkbox
                      checked={value.isDefault}
                      disabled={value.isDefault}
                      onChange={(e) =>
                        handleDefaultChange(index, e.target.checked)
                      }
                    />
                  }
                />
              ),
              description: (
                <MDBox sx={{ width: "100%", minWidth }} fullWidth>
                  <TranslatableTextField
                    sx={{ width: "100%", minWidth }}
                    values={value.description || ""}
                    label={label("Description-singular")}
                    onChange={(e) =>
                      handleDescriptionChange(index, e.target.value)
                    }
                  />
                </MDBox>
              ),
              actions: (
                <IconButton
                  size="small"
                  onClick={() => {
                    setOpen({ alert: { index } });
                  }}
                >
                  <Icon>delete</Icon>
                </IconButton>
              ),
            })),
          }}
          isSorted={false}
          entriesPerPage={false}
          showTotalEntries={false}
          noEndBorder
        />
      </MDBox>
    </Grid>
  );
}

// Reusable KPI Form Component
function KPIForm({ kpi, setKpi, data, error, can, dropdownOptions }) {
  const { label } = useLanguage();
  const { user } = useAuth();

  const [selectedAccount, setSelectedAccount] = React.useState(
    dropdownOptions.accounts?.find((a) => a.id == user.accountId)?.id,
  );

  const competencyDropdownOptions = React.useMemo(() => {
    return dropdownOptions.competencies?.filter(
      (c) => c.accountId == kpi.accountId,
    );
  }, [dropdownOptions.competencies, kpi?.accountId]);

  React.useEffect(() => {
    setSelectedAccount(
      dropdownOptions.accounts?.find((a) => a.id == user.accountId)?.id,
    );
  }, [dropdownOptions.accounts]);

  console.log("Dropdown optionss: ", dropdownOptions);

  return (
    <Grid container spacing={2} mt={-0.5}>
      <AccountSelectionGrid
        accountId={kpi.accountId}
        setAccountId={setKpi}
        dropdownOptions={dropdownOptions}
        can={can}
        entitySetter={setKpi}
      />

      <Grid item xs={12}>
        <TranslatableTextField
          multiline
          rows={3}
          autoFocus
          required
          id="kpiDescription"
          name="kpiDescription"
          label={label("Description-singular")}
          type="text"
          values={kpi.description}
          onChange={(e) => {
            setKpi({
              ...kpi,
              description: e.target.value,
            });
          }}
          fullWidth
        />
      </Grid>

      <Grid item xs={6}>
        <MDInput
          required
          id="reference code-input"
          margin="dense"
          label={label("reference-code-singular")}
          type="text"
          value={kpi.referenceCode}
          error={error?.fields === "kpi_referenceCode_key"}
          helperText={
            error?.fields === "kpi_referenceCode_key" && error?.message
          }
          onChange={(e) => {
            setKpi({ ...kpi, referenceCode: e.target.value });
          }}
          fullWidth
        />
      </Grid>

      <Grid item xs={6}>
        <MDInput
          required
          margin="dense"
          id="kpiWeight"
          name="kpiWeight"
          label={label("weight-singular")}
          type="number"
          value={kpi.weight}
          onChange={(e) => {
            setKpi({ ...kpi, weight: e.target.value });
          }}
          fullWidth
        />
      </Grid>

      <Grid item xs={6}>
        <MSelect
          required
          id="type-select"
          value={kpi.type}
          label={label("kpi-type-singular")}
          items={["Static", "Dynamic"].map((type, index) => ({
            value: index,
            render: type,
          }))}
          onChange={(value) => {
            setKpi({ ...kpi, type: value });
          }}
        />
      </Grid>

      <Grid item xs={6}>
        <MSelect
          id="kpiValueTypeId-select"
          required
          value={kpi.kpiValueTypeId}
          label={label("Value-type-singular")}
          items={data.valueTypes.map((valueType) => ({
            value: valueType.id,
            render: valueType.description,
          }))}
          onChange={(value) => {
            setKpi({ ...kpi, kpiValueTypeId: value });
          }}
        />
      </Grid>

      <Grid item xs={6}>
        <DropdownSelect
          label={label("competency-singular")}
          items={competencyDropdownOptions}
          initialValue={kpi?.accountKpis?.[0]?.competencyId}
          onChange={(newValue) => {
            setKpi((kpi) => {
              const newKpi = { ...kpi };
              if (!newKpi.accountKpis) {
                newKpi.accountKpis = [];
              }

              if (!newKpi.accountKpis.length) {
                newKpi.accountKpis.push({
                  accountId: kpi.accountId,
                  competencyId: newValue?.id,
                });
              } else {
                newKpi.accountKpis[0] = {
                  ...(newKpi.accountKpis[0] || {}),
                  competencyId: newValue?.id,
                };
              }

              return newKpi;
            });
          }}
        />
      </Grid>

      {/* <Grid item xs={12}>
        <Card
          variant="outlined"
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            backgroundColor: "whitesmoke",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: "500", mt: -0.5 }}>
              Account specific properties
            </Typography>
            <DropdownSelect
              // label={label("account-singular")}
              items={dropdownOptions.accounts}
              initialValue={selectedAccount || ""}
              required
              onChange={(newValue) => {
                setSelectedAccount(newValue?.id);
              }}
              disableClearable
              sx={{ minWidth: "300px" }}
              inputProps={{ variant: "standard" }}
            />
          </Box>

          <Grid container spacing={1}>
            <Grid item xs={6}>
              <DropdownSelect
                label={label("competency-singular")}
                items={competencyDropdownOptions}
                initialValue={
                  kpi?.accountKpis?.find?.(
                    (accountKpi) => accountKpi.accountId == selectedAccount,
                  )?.competencyId
                }
                required
                onChange={(newValue) => {
                  setKpi((kpi) => {
                    const newKpi = { ...kpi };
                    if (!newKpi.accountKpis) {
                      newKpi.accountKpis = [];
                    }

                    const accountKpiIndex = newKpi.accountKpis.findIndex(
                      (accountKpi) => accountKpi.accountId == selectedAccount,
                    );
                    if (accountKpiIndex == -1) {
                      newKpi.accountKpis.push({
                        accountId: selectedAccount,
                        competencyId: newValue?.id,
                      });
                    } else {
                      newKpi.accountKpis[accountKpiIndex] = {
                        ...(newKpi.accountKpis[accountKpiIndex] || {}),
                        competencyId: newValue?.id,
                      };
                    }

                    return newKpi;
                  });
                }}
              />
            </Grid>
          </Grid>
        </Card>
      </Grid> */}

      {kpi.kpiValueTypeId == 2 ? (
        <>
          <Grid item xs={6}>
            <FormControlLabel
              label={"Eliminatory"}
              control={
                <Checkbox
                  checked={!!kpi.eliminatory}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setKpi({ ...kpi, eliminatory: true });
                    } else {
                      setKpi({ ...kpi, eliminatory: false });
                    }
                  }}
                />
              }
            />
          </Grid>

          <EditableItemsTable
            title={label("deviation-plural")}
            items={kpi.deviationsDescription}
            onItemsChange={(items) =>
              setKpi({ ...kpi, deviationsDescription: items })
            }
            addButtonText={label("add-deviation-button")}
            type={2}
          />

          <EditableItemsTable
            title={label("action-plural")}
            items={kpi.actions}
            onItemsChange={(items) => setKpi({ ...kpi, actions: items })}
            addButtonText={label("add-action-button")}
            type={1}
          />

          <EditableItemsTable
            title={label("risk-plural")}
            items={kpi.risks}
            onItemsChange={(items) => setKpi({ ...kpi, risks: items })}
            addButtonText={label("add-risk-button")}
            type={0}
          />
        </>
      ) : (
        <>
          <Grid item xs={6}>
            <FormControlLabel
              label={"Force voice recording"}
              control={
                <Checkbox
                  checked={!!kpi.forceVoiceRecording}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setKpi({ ...kpi, forceVoiceRecording: true });
                    } else {
                      setKpi({ ...kpi, forceVoiceRecording: false });
                    }
                  }}
                />
              }
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              label={"Force image"}
              control={
                <Checkbox
                  checked={!!kpi.forceImage}
                  onChange={(e) => {
                    console.log("CHECKED: ", e.target.checked);
                    if (e.target.checked) {
                      setKpi({ ...kpi, forceImage: true });
                    } else {
                      setKpi({ ...kpi, forceImage: false });
                    }
                  }}
                />
              }
            />
          </Grid>
        </>
      )}
    </Grid>
  );
}

// Reusable Pillar Form Component
function PillarForm({
  pillar,
  setPillar,
  data,
  error,
  can,
  dropdownOptions,
  handleCategoryChange,
  isTabPanel = false,
}) {
  const { label } = useLanguage();
  return (
    <Grid container spacing={2} mt={isTabPanel ? 0 : -0.5}>
      <AccountSelectionGrid
        accountId={pillar?.accountId}
        setAccountId={setPillar}
        dropdownOptions={dropdownOptions}
        can={can}
        entitySetter={setPillar}
      />

      <Grid item xs={12}>
        <TranslatableTextField
          multiline
          rows={3}
          autoFocus
          values={pillar?.description ? pillar.description : ""}
          onChange={(e) => {
            setPillar({ ...pillar, description: e.target.value });
          }}
          required
          margin="dense"
          id="pillarDescription"
          name="pillarDescription"
          label={label("Description-singular")}
          type="text"
          fullWidth
        />
      </Grid>

      <Grid item xs={6}>
        <MDInput
          required
          value={pillar?.referenceCode ? pillar.referenceCode : ""}
          onChange={(e) => {
            setPillar({ ...pillar, referenceCode: e.target.value });
          }}
          id="pillarReferenceCode"
          name="pillarReferenceCode"
          label={label("Reference-code-singular")}
          error={error?.fields === "pillar_referenceCode_key"}
          helperText={
            error?.fields === "pillar_referenceCode_key" && error?.message
          }
          type="text"
          fullWidth
        />
      </Grid>

      <Grid item xs={6}>
        {isTabPanel ? (
          <MDGroupedSelect
            required
            fullWidth={true}
            label={label("Category-plural")}
            readOnly={data?.subcategoryId ? true : false}
            value={pillar?.subcategoryId}
            items={data.categories.map((item) => ({
              name: item.description,
              options: item.subcategories.map((subcategory) => ({
                name: subcategory.description,
                value: subcategory.id,
              })),
            }))}
            onChange={handleCategoryChange}
          />
        ) : (
          <GroupedSelectMultiple
            required
            fullWidth={true}
            label={label("Category-plural")}
            readOnly={data?.parentId ? true : false}
            value={
              pillar?.subcategories.length > 0 && pillar?.subcategories[0].id
                ? pillar.subcategories.map((sub) => sub.id)
                : pillar.subcategories
            }
            items={data.categories.map((item) => ({
              render: item.description,
              children: item.subcategories.map((subcategory) => ({
                render: subcategory.description,
                value: subcategory.id,
              })),
            }))}
            onChange={handleCategoryChange}
          />
        )}
      </Grid>
    </Grid>
  );
}

export default function CreatePillarDialog({
  title,
  isOpen,
  onSubmit,
  data,
  createChoice,
  type,
  onClose,
}) {
  const { label } = useLanguage();
  const [open, setOpen] = React.useState(false);
  const [state, setState] = React.useState(0);
  const [pillar, setPillar] = React.useState({
    parentId: data.parentId,
    subcategoryId: data.subcategoryId,
    subcategories: data.subcategories
      ? data.subcategories?.map((sub) => sub.id)
      : [],
  });
  const [options, setOptions] = React.useState({
    accounts: [],
    competencies: [],
  });
  const [fetchLoading, setFetchLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [submissionError, setSubmissionError] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const [kpi, setKpi] = React.useState({
    type: 0,
    pillarId: data.pillarId,
    subcategoryId: data.subcategoryId,
    kpiValueType: data.valueType,
    weight: 0,
    referenceCode: 0,
    deviationDescription: "",
    risks: [],
    actions: [],
    deviationsDescription: [],
    accountKpis: [],
  });

  const can = usePermissionMonitor();

  async function fetch() {
    let [ok, data] = await accountsService.getAccounts({ raw: true });
    if (ok) {
      setOptions((options) => ({ ...options, accounts: [...data] }));
    }

    [ok, data] = await competencyService.getCompetencies();
    if (ok && !data.error) {
      setOptions((options) => ({ ...options, competencies: [...data] }));
    }
  }

  const getEntity = async (data) => {
    if (!data) return;

    setFetchLoading(true);
    if (type == "edit" && (data?.pillar?.id || data?.kpi?.id)) {
      if (createChoice == "pillar") {
        const [response, pillar] = await pillarsService.getPillar(
          data.pillar.id,
        );
        if (response.ok) {
          data = {
            ...data,
            pillar: {
              ...data.pillar,
              ...pillar,
            },
          };
        }
      } else if (createChoice == "kpi") {
        const [response, kpi] = await kpisService.getKpi(data.kpi.id);
        if (response.ok) {
          data = {
            ...data,
            kpi: {
              ...(data.kpi || {}),
              ...kpi,
            },
          };
        }
      }
    }

    if (createChoice == "pillar") {
      setPillar(data.pillar || {
        referenceCode: await generatePillarReferenceCode(),
        accountId: null,
        parentId: data.parentId,
        subcategoryId: data.subcategoryId ?? data.categories[0]?.id ?? null,
        subcategories: data.subcategories?.map((s) => s.id) ?? [],
        description: {},
      });
    } else if (createChoice == "kpi") {
      setKpi(data.kpi ? {
        id: data.kpi.id,
        type: data.kpi.type,
        pillarId: data.kpi.pillarId,
        subcategoryId: data.kpi.subcategoryId,
        kpiValueTypeId: data.kpi.kpiValueTypeId,
        weight: data.kpi.weight,
        referenceCode: data.kpi.referenceCode,
        deviationDescription: data.kpi.deviationDescription,
        description: data.kpi.description,
        accountId: data.kpi.accountId,
        eliminatory: data.kpi.eliminatory,
        forceImage: data.kpi.forceImage,
        forceVoiceRecording: data.kpi.forceVoiceRecording,
        risks: data.kpi.kpiRiskActions.filter((r) => r.type === 0),
        actions: data.kpi.kpiRiskActions.filter((r) => r.type === 1),
        deviationsDescription: data.kpi.kpiRiskActions.filter(
          (r) => r.type === 2,
        ),
        accountKpis: data.kpi.accountKpis,
      } : {
        type: 0,
        pillarId: data.pillarId,
        subcategoryId: data.subcategoryId,
        kpiValueTypeId: data.valueTypes[0]?.id ?? null,
        weight: 0,
        accountId: null,
        referenceCode: await generateKpiReferenceCode(data.parent?.id),
        description: { en: "" },
        risks: [],
        actions: [],
        deviationsDescription: [],
        accountKpis: [],
      });
    }

    setFetchLoading(false);
  };

  React.useEffect(() => {
    if (!isOpen) return;
    setOpen(true);
    setError(null);
    setLoading(false);
  }, [isOpen]);

  React.useEffect(() => {
    setState(!createChoice ? state : createChoice == "pillar" ? 0 : 1);
  }, [createChoice]);

  React.useEffect(() => {
    getEntity(data);
  }, [data]);

  React.useEffect(() => {
    fetch();
  }, []);

  const dropdownOptions = React.useMemo(() => {
    return {
      accounts:
        options.accounts?.map((v) => ({ id: v.id, label: v.name })) || [],
      competencies:
        options.competencies?.map((v) => ({
          id: v.id,
          label: v.name,
          accountId: v.accountId,
        })) || [],
    };
  }, [options]);

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else setOpen(false);
  };

  function handleCategoryChange(newSubcategories) {
    setPillar({ ...pillar, subcategories: [...newSubcategories] });
  }

  return (
    <Dialog
      maxWidth={"lg"}
      open={open}
      onClose={handleClose}
      PaperProps={{
        component: "form",
        onSubmit: async (event) => {
          event.preventDefault();
          if (submissionError) {
            console.log(submissionError);
            return;
          }

          setLoading(true);
          if (state == 0) {
            const p = { ...pillar };
            p.isPillar = true;
            if (type === "edit") {
              p.subcategories = p.subcategories.map((sub) => {
                if (sub.id) {
                  return sub;
                } else return { id: sub };
              });
            }
            const result = await onSubmit(p);
            if (result.error) {
              setError(result.error);
            } else {
              setError(null);
              handleClose();
            }
          } else {
            const k = { ...kpi };
            k.isPillar = false;
            const result = await onSubmit(k);
            if (result.error) {
              setError(result.error);
              console.log(result.error);
            } else {
              setError(null);
              handleClose();
            }
          }
          setLoading(false);
        },
      }}
    >
      <DialogTitle>
        {type == "edit" && createChoice
          ? createChoice == "kpi"
            ? label("edit-kpi")
            : label("edit-pillar")
          : null}
        {type != "edit" && createChoice
          ? createChoice == "kpi"
            ? label("create-kpi")
            : label("create-pillar")
          : null}
      </DialogTitle>
      <DialogContent>
        {fetchLoading ? (
          label("loading")
        ) : (
          <>
            {createChoice == "kpi" && (
              <KPIForm
                kpi={kpi}
                setKpi={setKpi}
                data={data}
                error={error}
                can={can}
                dropdownOptions={dropdownOptions}
              />
            )}

            {createChoice == "pillar" && pillar && (
              <PillarForm
                pillar={pillar}
                setPillar={setPillar}
                data={data}
                error={error}
                can={can}
                dropdownOptions={dropdownOptions}
                handleCategoryChange={handleCategoryChange}
              />
            )}

            {!createChoice && (
              <TabPanel
                items={[
                  {
                    label: label("create-pillar"),
                    panel: (
                      <PillarForm
                        pillar={pillar}
                        setPillar={setPillar}
                        data={data}
                        error={error}
                        can={can}
                        dropdownOptions={dropdownOptions}
                        handleCategoryChange={handleCategoryChange}
                        isTabPanel={true}
                      />
                    ),
                  },
                  {
                    label: label("create-kpi"),
                    panel: (
                      <KPIForm
                        kpi={kpi}
                        setKpi={setKpi}
                        data={data}
                        error={error}
                        can={can}
                        dropdownOptions={dropdownOptions}
                      />
                    ),
                  },
                ]}
                value={state}
                onChange={(e, value) => {
                  setState(value);
                }}
              />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{label("cancel-button")}</Button>
        <LoadingButton
          loading={loading}
          variant="contained"
          color="info"
          type="submit"
        >
          {type == "edit" ? label("save-button") : label("create-button")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

// function generateDefaultReferenceCode(parentId, id) {
//   function convert(n) {
//     var result = "";
//     n--;
//     do {
//       result = ((n % 26) + 10).toString(36) + result;
//       n = Math.floor(n / 26) - 1;
//     } while (n >= 0);
//   }

//   if (parentId) {
//     return convert(parentId) + id;
//   } else {
//     return convert(id);
//   }
// }

// function generateKpiReferenceCode(parent) {
//   return parent?.referenceCode + (parent?.kpis?.length + 1);
// }

async function generatePillarReferenceCode() {
  const [response, data] = await pillarsService.getPillarReferenceCode();

  if (response.ok) {
    return data.referenceCode;
  }
}

async function generateKpiReferenceCode(parentId) {
  if (!parentId) return;

  const [response, data] = await kpisService.getKpiReferenceCode(parentId);

  if (response.ok) {
    return data.referenceCode;
  }
}
